import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  ArrowLeft, Edit, Trash2, Calendar, Archive, ArchiveRestore, Cog, 
  Hash, Package, RulerDimensionLine, Info, AlertCircle, ClipboardList, Tag,
  ExternalLink, FileText, Clock, User, Bookmark
} from 'lucide-react';
import Header from './Header';
import BrandBadge from './BrandBadge';
import EquipmentForm from './EquipmentForm';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, getOrderTypeDisplayText, getStatusColor, getAllOrders } from '../lib/orderService';
import { getAllBrands } from '../lib/brandService';
import { getAllSpecs } from '../lib/specService';
import { getAllEquipment } from '../lib/equipmentService';

const EquipmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const location = useLocation();
  const [equipment, setEquipment] = useState(null);
  const [brand, setBrand] = useState(null);
  const [orders, setOrders] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isEquipmentInUse, setIsEquipmentInUse] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Filter state
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  // Get return URL from search parameters
  const getReturnUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get('returnUrl');
    return returnUrl ? decodeURIComponent(returnUrl) : '/equipment';
  };

  useEffect(() => {
    fetchEquipmentData();
  }, [id]);

  // Reset pagination when orders change or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [orders.length, showBookmarkedOnly]);

  const fetchEquipmentData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch equipment details using local-first service
      const equipmentList = await getAllEquipment(true); // Include archived
      const equipmentData = equipmentList.find(item => item.id === id);
      
      if (!equipmentData) {
        setError('Equipo no encontrado');
        setLoading(false);
        return;
      }

      setEquipment(equipmentData);

      // Fetch brand information using local-first service
      if (equipmentData.marcaId || equipmentData.marca) {
        try {
          const brandsList = await getAllBrands();
          let brandData = null;
          
          // Try to find by ID first (new format)
          if (equipmentData.marcaId) {
            brandData = brandsList.find(brand => brand.id === equipmentData.marcaId);
          }
          
          // If not found by ID, try to find by name (backward compatibility)
          if (!brandData && equipmentData.marca) {
            brandData = brandsList.find(brand => brand.nombre === equipmentData.marca);
          }
          
          // Set brand or fallback
          setBrand(brandData || { 
            nombre: equipmentData.marca || 'Sin marca', 
            color: '#6B7280' 
          });
        } catch (error) {
          console.log('Could not fetch brand data, using fallback');
          setBrand({ 
            nombre: equipmentData.marca || 'Sin marca', 
            color: '#6B7280' 
          });
        }
      }

      // Fetch orders that use this equipment using local-first service
      try {
        const allOrders = await getAllOrders(); // Use cached data, will include archived orders from cache
        
        // Filter orders that contain this equipment
        const ordersWithEquipment = allOrders.filter(order => {
          if (order.equipos && Array.isArray(order.equipos)) {
            return order.equipos.some(equipo => equipo.catalogId === id);
          }
          return false;
        });
        
        // Orders already have client information expanded from orderService
        // Sort by folio (descending - highest to lowest)
        ordersWithEquipment.sort((a, b) => {
          const folioA = parseInt(a.folio) || 0;
          const folioB = parseInt(b.folio) || 0;
          return folioB - folioA; // Descending order
        });
        
        setOrders(ordersWithEquipment);
        setIsEquipmentInUse(ordersWithEquipment.length > 0);
      } catch (error) {
        console.error('Error fetching orders:', error);
        setOrders([]);
      }

      // Fetch specifications using local-first service
      try {
        const specsList = await getAllSpecs();
        setSpecs(specsList);
      } catch (error) {
        console.log('Could not fetch specs data, using default order');
        setSpecs([]);
      }
      
      // Check if equipment is used in orders
      const inUse = await checkEquipmentInOrders();
      setIsEquipmentInUse(inUse);
      
    } catch (error) {
      console.error('Error fetching equipment data:', error);
      setError('Error al cargar la información del equipo');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    const months = [
      'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
      'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'
    ];
    
    const day = dateObj.getUTCDate().toString().padStart(2, '0');
    const month = months[dateObj.getUTCMonth()];
    const year = dateObj.getUTCFullYear();
    
    return `${day}/${month}/${year}`;
  };

  const canEdit = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const canDelete = () => {
    return userRole === 'admin';
  };

  // Helper function to get order type colors
  const getOrderTypeColor = (tipo) => {
    const typeColors = {
      'Reparación': 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
      'Ensamble': 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400',
      'Garantía': 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400',
      'Fabricación': 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
    };
    
    return typeColors[tipo] || 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  };

  const getClientDisplayName = (order) => {
    if (order.clienteName) {
      return order.clienteName;
    }
    if (order.cliente) {
      return `Cliente: ${order.cliente}`;
    }
    return 'Sin cliente';
  };

  const renderClientInfo = (order) => {
    // For company clients, display company name bold, contact name, and phone on separate lines
    if (order.clienteCompanyName && order.clienteContactName) {
      return (
        <div className="max-w-xs">
          <div className="text-sm font-bold leading-tight truncate" title={order.clienteCompanyName}>
            {order.clienteCompanyName}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 leading-tight truncate" title={order.clienteContactName}>
            {order.clienteContactName}
          </div>
          {order.clientePhone && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {order.clientePhone}
            </div>
          )}
        </div>
      );
    }
    
    // For individual clients or fallback, use the existing display
    return (
      <div className="max-w-xs">
        <div className="text-sm leading-tight truncate" title={getClientDisplayName(order)}>
          {getClientDisplayName(order)}
        </div>
        {order.clientePhone && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {order.clientePhone}
          </div>
        )}
      </div>
    );
  };

  const handleEdit = () => {
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setSuccess('Equipo actualizado exitosamente');
    setShowForm(false);
    fetchEquipmentData(); // Refresh equipment data
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleFormClose = () => {
    setShowForm(false);
  };

  const handleArchive = async () => {
    if (!window.confirm(`¿Está seguro de archivar el equipo ${equipment.modelo}?`)) return;

    try {
      setDeleteLoading(true);
      await updateDoc(doc(db, 'equipment_catalog', equipment.id), {
        isArchived: true,
        updatedAt: new Date()
      });
      setSuccess(`${equipment.modelo} ha sido archivado`);
      await fetchEquipmentData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error archiving equipment:', error);
      setError('Error al archivar el equipo');
      setTimeout(() => setError(''), 5000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleUnarchive = async () => {
    if (!window.confirm(`¿Está seguro de desarchivar el equipo ${equipment.modelo}?`)) return;

    try {
      setDeleteLoading(true);
      await updateDoc(doc(db, 'equipment_catalog', equipment.id), {
        isArchived: false,
        updatedAt: new Date()
      });
      setSuccess(`${equipment.modelo} ha sido desarchivado`);
      await fetchEquipmentData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error unarchiving equipment:', error);
      setError('Error al desarchivar el equipo');
      setTimeout(() => setError(''), 5000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const checkEquipmentInOrders = async () => {
    try {
      // Use local-first service to check if equipment is used in orders
      const allOrders = await getAllOrders(); // Use cached data, will include archived orders from cache
      
      // Check each order's equipos array for the equipment
      for (const order of allOrders) {
        if (order.equipos && Array.isArray(order.equipos)) {
          for (const equipo of order.equipos) {
            if (equipo.catalogId === id) {
              return true; // Equipment found in orders
            }
          }
        }
      }
      
      return false; // Equipment not found in any orders
    } catch (error) {
      console.error('Error checking equipment in orders:', error);
      return true; // Assume equipment is in use if there's an error
    }
  };

  const handleDelete = async () => {
    if (!canDelete()) {
      setError('No tienes permisos para eliminar equipos');
      setTimeout(() => setError(''), 5000);
      return;
    }

    try {
      setDeleteLoading(true);

      // Check if equipment is used in any orders
      const isInUse = await checkEquipmentInOrders();
      
      if (isInUse) {
        setError('No se puede eliminar este equipo porque está siendo usado en una o más órdenes de trabajo. Debe archivar el equipo en su lugar.');
        setTimeout(() => setError(''), 8000);
        setDeleteLoading(false);
        return;
      }

      // Confirm deletion
      if (!window.confirm(
        `¿Está seguro de eliminar permanentemente el equipo ${equipment.modelo}?\n\n` +
        `Esta acción no se puede deshacer. Si desea mantener el historial, ` +
        `considere archivar el equipo en su lugar.`
      )) {
        setDeleteLoading(false);
        return;
      }

      // Delete the equipment
      await deleteDoc(doc(db, 'equipment_catalog', id));
      setSuccess(`${equipment.modelo} ha sido eliminado permanentemente`);
      
      // Navigate back to equipment list after short delay
      setTimeout(() => navigate(getReturnUrl()), 1500);
    } catch (error) {
      console.error('Error deleting equipment:', error);
      setError('Error al eliminar el equipo');
      setTimeout(() => setError(''), 5000);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-6xl mx-auto p-6">
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Cargando información del equipo...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-6xl mx-auto p-6">
          <div className="mb-6">
            <Link 
              to={getReturnUrl()}
              className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Volver al Catálogo</span>
            </Link>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!equipment) {
    return null;
  }

  // Filter and pagination calculations
  const filteredOrders = showBookmarkedOnly 
    ? orders.filter(order => order.isBookmarked === true)
    : orders;
  
  const totalItems = filteredOrders.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOrders = filteredOrders.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const hasAlternateParts = equipment.partesAlternativas && equipment.partesAlternativas.length > 0;
  const hasSpecs = equipment.especificaciones && Object.keys(equipment.especificaciones).length > 0;

  // Get specifications in priority order
  const getOrderedSpecifications = () => {
    if (!hasSpecs) return [];
    
    // Create a map of spec names to their priority for quick lookup
    const specPriorityMap = {};
    specs.forEach(spec => {
      specPriorityMap[spec.nombre] = spec.prioridad || 999; // Default high priority for specs without priority
    });
    
    // Convert equipment specs to array and sort by priority
    const equipmentSpecs = Object.entries(equipment.especificaciones);
    
    return equipmentSpecs.sort((a, b) => {
      const priorityA = specPriorityMap[a[0]] || 999;
      const priorityB = specPriorityMap[b[0]] || 999;
      return priorityA - priorityB; // Lower priority number = higher priority (displayed first)
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <Link 
            to={getReturnUrl()}
            className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Volver al Catálogo</span>
          </Link>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md">
            {success}
          </div>
        )}

        {/* Equipment Information Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Package className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {equipment.modelo}
                  </h1>
                  <div className="flex items-center space-x-2 mt-1">
                    {brand && <BrandBadge brand={brand} size="md" />}
                    <span className="text-lg text-gray-600 dark:text-gray-400">-</span>
                    <span className="text-lg text-gray-600 dark:text-gray-400">{equipment.tipo}</span>
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    {equipment.isArchived && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                        <Archive className="h-3 w-3 mr-1" />
                        Archivado
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              {canEdit() && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleEdit}
                    disabled={deleteLoading}
                    className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded disabled:opacity-50"
                    title="Editar equipo"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  
                  {equipment.isArchived ? (
                    <button
                      onClick={handleUnarchive}
                      disabled={deleteLoading}
                      className="p-2 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded disabled:opacity-50"
                      title="Desarchivar equipo"
                    >
                      <ArchiveRestore className="h-5 w-5" />
                    </button>
                  ) : (
                    <button
                      onClick={handleArchive}
                      disabled={deleteLoading}
                      className="p-2 text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded disabled:opacity-50"
                      title="Archivar equipo"
                    >
                      <Archive className="h-5 w-5" />
                    </button>
                  )}
                  
                  {canDelete() && (
                    <button
                      onClick={!isEquipmentInUse ? handleDelete : undefined}
                      disabled={deleteLoading || isEquipmentInUse}
                      className={`p-2 rounded disabled:opacity-50 ${
                        isEquipmentInUse
                          ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                          : 'text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                      }`}
                      title={
                        isEquipmentInUse
                          ? 'No se puede eliminar: equipo usado en órdenes de trabajo'
                          : 'Eliminar equipo'
                      }
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Información Básica</h3>
                
                <div className="flex items-center space-x-3">
                  <Tag className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Marca</p>
                    <div className="mt-1">
                      {brand ? (
                        <BrandBadge brand={brand} size="sm" />
                      ) : (
                        <span className="text-sm text-gray-600 dark:text-gray-400">{equipment.marcaId || 'Sin marca'}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Cog className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Tipo</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{equipment.tipo}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Info className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Modelo</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{equipment.modelo}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Fecha de Registro</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{formatDate(equipment.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Part Numbers */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Números de Parte</h3>
                
                <div className="flex items-start space-x-3">
                  <Hash className="h-5 w-5 text-gray-400 dark:text-gray-500 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Número Principal</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-700 px-2 py-1 rounded mt-1">
                      {equipment.numeroParte}
                    </p>
                  </div>
                </div>

                {hasAlternateParts ? (
                  <div className="flex items-start space-x-3">
                    <Hash className="h-5 w-5 text-gray-400 dark:text-gray-500 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Números Alternativos ({equipment.partesAlternativas.length})
                      </p>
                      <div className="mt-2 space-y-1">
                        {equipment.partesAlternativas.map((part, index) => (
                          <p key={index} className="text-sm text-gray-600 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-700 px-2 py-1 rounded">
                            {part}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start space-x-3">
                    <Hash className="h-5 w-5 text-gray-400 dark:text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Números Alternativos</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">No hay números alternativos registrados</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Status Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Estado</h3>
                
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Estado de Archivo</p>
                    <p className={`text-sm ${equipment.isArchived ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {equipment.isArchived ? 'Archivado' : 'Activo'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Última Actualización</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{formatDate(equipment.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Specifications Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <RulerDimensionLine className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Especificaciones Técnicas</h2>
            </div>
          </div>

          <div className="p-6">
            {hasSpecs ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getOrderedSpecifications().map(([spec, value]) => (
                  <div key={spec} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">{spec}</h4>
                      {equipment.esCustomizable?.[spec] && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                          <Cog className="h-3 w-3 mr-1" />
                          Customizable
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 font-mono bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-200 dark:border-gray-600">
                      {value || 'No especificado'}
                    </p>
                    {equipment.esCustomizable?.[spec] && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Se puede personalizar por orden de trabajo
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <RulerDimensionLine className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">Sin Especificaciones</p>
                <p>No hay especificaciones técnicas registradas para este equipo.</p>
              </div>
            )}
          </div>
        </div>

        {/* Usage History Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <ClipboardList className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Historial de Uso</h2>
                <span className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  {showBookmarkedOnly ? filteredOrders.length : orders.length} órdenes
                </span>
              </div>
              
              {orders.length > 0 && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showBookmarkedOnly"
                    checked={showBookmarkedOnly}
                    onChange={(e) => setShowBookmarkedOnly(e.target.checked)}
                    className="h-4 w-4 text-yellow-600 dark:text-yellow-400 focus:ring-yellow-500 dark:focus:ring-yellow-400 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                  />
                  <label htmlFor="showBookmarkedOnly" className="ml-2 flex items-center text-sm text-gray-900 dark:text-white">
                    <Bookmark className="h-4 w-4 mr-1" />
                    Solo favoritas
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="p-6">
            {filteredOrders.length > 0 ? (
              <>
                {/* Mobile Card Layout */}
                <div className="block md:hidden space-y-4">
                  {currentOrders.map((order) => (
                    <div 
                      key={order.id} 
                      className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="flex items-center space-x-2">
                            <div className="font-semibold text-gray-900 dark:text-white text-lg">{order.folio}</div>
                            {order.isBookmarked && (
                              <Bookmark className="h-4 w-4 text-yellow-600 dark:text-yellow-400 fill-current" title="Orden favorita" />
                            )}
                          </div>
                          {order.isArchived && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-300 mt-1">
                              Archivada
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col items-end space-y-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOrderTypeColor(order.tipo_de_orden)}`}>
                            {getOrderTypeDisplayText(order.tipo_de_orden)}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status_orden)}`}>
                            {order.status_orden || 'Sin estado'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Fecha:</span>
                          <div className="text-right">
                            <div className="text-gray-900 dark:text-white">{formatDate(order.fecha)}</div>
                          </div>
                        </div>
                        
                        {(order.clienteName || order.cliente || order.clienteCompanyName) && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Cliente:</span>
                            <div className="text-right text-xs">
                              {renderClientInfo(order)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tablet and Desktop Table Layout */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Folio
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Fecha
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Tipo
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                          Cliente
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-48">
                          Estado
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {currentOrders.map((order) => (
                        <tr key={order.id} className={`cursor-pointer ${
                            order.isArchived 
                              ? 'bg-gray-100 dark:bg-gray-900 hover:bg-gray-200 dark:hover:bg-gray-800' 
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`} onClick={() => navigate(`/orders/${order.id}`)}>
                          <td className="px-3 py-4 text-sm">
                            <div className="flex items-center space-x-2">
                              <div className="font-semibold text-gray-900 dark:text-white">{order.folio}</div>
                              {order.isBookmarked && (
                                <Bookmark className="h-4 w-4 text-yellow-600 dark:text-yellow-400 fill-current" title="Orden favorita" />
                              )}
                            </div>
                            {order.isArchived && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-300 mt-1">
                                Archivada
                              </span>
                            )}
                            {/* Client info for medium screens */}
                            <div className="lg:hidden mt-2 space-y-1">
                              <div className="text-xs text-gray-600 dark:text-gray-400">
                                {renderClientInfo(order)}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-900 dark:text-white">
                            {formatDate(order.fecha)}
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOrderTypeColor(order.tipo_de_orden)}`}>
                              {getOrderTypeDisplayText(order.tipo_de_orden)}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-900 dark:text-white hidden lg:table-cell">
                            {renderClientInfo(order)}
                          </td>
                          <td className="px-3 py-4 text-sm w-48">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(order.status_orden)}`}>
                              {order.status_orden || 'Sin estado'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                    {/* Mobile pagination */}
                    <div className="flex flex-1 justify-between sm:hidden">
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Siguiente
                      </button>
                    </div>

                    {/* Desktop pagination */}
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
                          <span className="font-medium">{Math.min(endIndex, totalItems)}</span> de{' '}
                          <span className="font-medium">{totalItems}</span> órdenes
                        </p>
                      </div>
                      <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <button
                            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="sr-only">Anterior</span>
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                            </svg>
                          </button>

                          {(() => {
                            const pageNumbers = [];
                            const showEllipsis = totalPages > 7;
                            
                            if (showEllipsis) {
                              if (currentPage <= 3) {
                                for (let i = 1; i <= Math.min(5, totalPages); i++) {
                                  pageNumbers.push(i);
                                }
                                if (totalPages > 5) {
                                  pageNumbers.push('...');
                                  pageNumbers.push(totalPages);
                                }
                              } else if (currentPage >= totalPages - 2) {
                                pageNumbers.push(1);
                                pageNumbers.push('...');
                                for (let i = Math.max(totalPages - 4, 1); i <= totalPages; i++) {
                                  if (i > 1) pageNumbers.push(i);
                                }
                              } else {
                                pageNumbers.push(1);
                                pageNumbers.push('...');
                                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                                  pageNumbers.push(i);
                                }
                                pageNumbers.push('...');
                                pageNumbers.push(totalPages);
                              }
                            } else {
                              for (let i = 1; i <= totalPages; i++) {
                                pageNumbers.push(i);
                              }
                            }
                            
                            return pageNumbers.map((number, index) => (
                              number === '...' ? (
                                <span key={`ellipsis-${index}`} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-offset-0">
                                  ...
                                </span>
                              ) : (
                                <button
                                  key={number}
                                  onClick={() => handlePageChange(number)}
                                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 ${
                                    currentPage === number
                                      ? 'z-10 bg-blue-600 dark:bg-blue-700 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:focus-visible:outline-blue-400'
                                      : 'text-gray-900 dark:text-gray-300'
                                  }`}
                                >
                                  {number}
                                </button>
                              )
                            ));
                          })()}

                          <button
                            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="sr-only">Siguiente</span>
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <ClipboardList className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                {orders.length === 0 ? (
                  <>
                    <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">Sin Historial de Uso</p>
                    <p>Este equipo no ha sido utilizado en ninguna orden de trabajo.</p>
                    <p className="text-sm mt-2">Las órdenes que incluyan este equipo aparecerán aquí.</p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">Sin Órdenes Favoritas</p>
                    <p>Este equipo no tiene órdenes marcadas como favoritas.</p>
                    <p className="text-sm mt-2">Desactiva el filtro para ver todas las órdenes.</p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Equipment Form Modal */}
      <EquipmentForm
        isOpen={showForm}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        editingEquipment={equipment}
      />
    </div>
  );
};

export default EquipmentDetail;